import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Target, Users, TrendingUp, DollarSign, BarChart3, Lightbulb, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const MarketingPage = () => {
  const navigate = useNavigate();

  const elements = [
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Market Research',
      description: 'Understanding your target audience, their needs, preferences, behaviors, and pain points through surveys, focus groups, and data analysis.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Target Audience',
      description: 'Defining and segmenting your ideal customers based on demographics, psychographics, and buying behaviors.'
    },
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: 'Brand Identity',
      description: 'Creating a unique brand personality, voice, visual identity, and positioning that resonates with your audience.'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Content Strategy',
      description: 'Developing valuable, relevant content that educates, entertains, and engages your target audience across channels.'
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: 'Analytics & Metrics',
      description: 'Measuring campaign performance using KPIs, ROI, conversion rates, and customer acquisition costs.'
    },
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: 'Budget Allocation',
      description: 'Strategically distributing resources across different marketing channels for maximum impact and return.'
    }
  ];

  const steps = [
    {
      step: '1',
      title: 'Define Your Goals',
      description: 'Establish clear, measurable objectives (SMART goals) such as increasing brand awareness, generating leads, or boosting sales.',
      tips: ['Set specific targets (e.g., 20% increase in leads)', 'Define timeline for achievement', 'Align goals with business objectives']
    },
    {
      step: '2',
      title: 'Know Your Audience',
      description: 'Create detailed buyer personas to understand who your customers are and what motivates their purchasing decisions.',
      tips: ['Conduct market research and surveys', 'Analyze competitor audiences', 'Identify pain points and desires']
    },
    {
      step: '3',
      title: 'Choose Your Channels',
      description: 'Select the most effective marketing channels based on where your target audience spends their time.',
      tips: ['Social media (Facebook, Instagram, LinkedIn)', 'Email marketing campaigns', 'Content marketing (blog, videos)', 'Paid advertising (Google Ads, social ads)', 'SEO and organic search']
    },
    {
      step: '4',
      title: 'Create Compelling Content',
      description: 'Develop high-quality, valuable content that addresses your audience\'s needs and positions you as an authority.',
      tips: ['Focus on solving customer problems', 'Use storytelling techniques', 'Include clear calls-to-action', 'Maintain consistent brand voice']
    },
    {
      step: '5',
      title: 'Set Your Budget',
      description: 'Allocate your marketing budget strategically across different channels and campaigns.',
      tips: ['Start with 7-10% of revenue for established businesses', 'Allocate more for new business launches (12-20%)', 'Track spending and adjust based on performance', 'Reserve budget for testing new channels']
    },
    {
      step: '6',
      title: 'Launch & Execute',
      description: 'Implement your marketing campaigns across chosen channels with consistent messaging and branding.',
      tips: ['Create a content calendar', 'Schedule posts and campaigns in advance', 'Ensure team alignment', 'Monitor launches closely']
    },
    {
      step: '7',
      title: 'Measure & Optimize',
      description: 'Track key metrics, analyze performance data, and continuously refine your strategy based on results.',
      tips: ['Monitor KPIs regularly', 'A/B test different approaches', 'Adjust budget allocation based on ROI', 'Learn from failures and successes']
    }
  ];

  const budgetGuidelines = [
    {
      category: 'Startups',
      percentage: '12-20%',
      description: 'Higher investment needed to build brand awareness and acquire initial customers'
    },
    {
      category: 'Growing Businesses',
      percentage: '7-12%',
      description: 'Moderate investment to scale customer acquisition and market presence'
    },
    {
      category: 'Established Brands',
      percentage: '5-10%',
      description: 'Maintenance marketing to retain market share and support growth initiatives'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={true} />
      
      {/* Hero Section */}
      <section className="relative bg-[#0d6571] pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:text-white/80 mb-8 -ml-4"
          >
            <ArrowLeft className="mr-2" />
            Back to Home
          </Button>
          
          <div className="flex items-center gap-4 mb-6">
            <Target className="w-16 h-16 text-[#d97841]" />
            <h1 className="text-5xl md:text-6xl font-bold text-white">
              Marketing Strategy Guide
            </h1>
          </div>
          
          <p className="text-xl text-white/90 max-w-4xl leading-relaxed">
            A comprehensive step-by-step guide to creating and executing effective marketing campaigns
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* What is Marketing */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What is Marketing?
            </h2>
            <div className="bg-gradient-to-br from-[#0d6571]/5 to-[#0d6571]/10 border-l-4 border-[#0d6571] p-8 rounded-r-lg">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Marketing is the process of creating, communicating, and delivering value to customers 
                for the purpose of promoting products, services, or ideas. It involves various strategies, 
                tactics, and activities aimed at understanding customer needs and preferences.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Marketing encompasses a wide range of activities that revolve around building and maintaining 
                customer relationships, ultimately driving sales and achieving business goals.
              </p>
            </div>
          </div>

          {/* Key Elements */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Key Elements of Marketing
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {elements.map((element, index) => (
                <div
                  key={index}
                  className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#0d6571] hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-[#d97841] mb-4">{element.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{element.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{element.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Step-by-Step Guide */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Step-by-Step Marketing Implementation
            </h2>
            <div className="space-y-8">
              {steps.map((stepItem, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-r from-white to-[#0d6571]/5 border-2 border-gray-200 rounded-xl p-8 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-start gap-6">
                    <div className="bg-[#0d6571] text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0">
                      {stepItem.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{stepItem.title}</h3>
                      <p className="text-lg text-gray-700 leading-relaxed mb-4">{stepItem.description}</p>
                      <div className="bg-white rounded-lg p-4 border border-[#0d6571]/20">
                        <p className="text-sm font-semibold text-[#0d6571] mb-2">Key Actions:</p>
                        <ul className="space-y-2">
                          {stepItem.tips.map((tip, tipIndex) => (
                            <li key={tipIndex} className="flex items-start text-gray-600">
                              <CheckCircle className="w-5 h-5 text-[#d97841] mr-2 flex-shrink-0 mt-0.5" />
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Budget Guidelines */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Marketing Budget Guidelines
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              As a general rule, businesses should allocate a percentage of their revenue to marketing. 
              Here's a breakdown based on business stage:
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {budgetGuidelines.map((guideline, index) => (
                <div
                  key={index}
                  className="bg-white border-2 border-[#0d6571] rounded-xl p-8 text-center hover:shadow-xl transition-all duration-300"
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{guideline.category}</h3>
                  <div className="text-5xl font-bold text-[#d97841] mb-4">{guideline.percentage}</div>
                  <p className="text-sm text-gray-600 italic">{guideline.description}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 bg-[#0d6571]/10 border-l-4 border-[#d97841] p-6 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed">
                <strong>Note:</strong> These percentages are of your gross revenue. The actual amount will vary 
                based on industry, competition, growth goals, and market conditions. Always track ROI and adjust 
                your budget based on what's working.
              </p>
            </div>
          </div>

          {/* Success Tips */}
          <div className="bg-[#0d6571] rounded-2xl p-12 text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">
              Tips for Marketing Success
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <CheckCircle className="w-6 h-6 text-[#d97841] mr-2" />
                  Do's
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">✓</span>
                    <span>Focus on customer value and solving problems</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">✓</span>
                    <span>Test different approaches and learn from data</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">✓</span>
                    <span>Build authentic relationships with your audience</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">✓</span>
                    <span>Stay consistent with your brand messaging</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#d97841] mr-2">✓</span>
                    <span>Invest in long-term strategies like SEO and content</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <CheckCircle className="w-6 h-6 text-red-400 mr-2" />
                  Don'ts
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">✗</span>
                    <span>Don't try to target everyone - focus matters</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">✗</span>
                    <span>Don't neglect measuring results and ROI</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">✗</span>
                    <span>Don't spam or use aggressive sales tactics</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">✗</span>
                    <span>Don't ignore customer feedback and reviews</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-2">✗</span>
                    <span>Don't expect instant results - be patient</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MarketingPage;
